import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { downloadAsCsv } from '@/lib/csv-export';

/**
 * `/leave/records` (TD-28 §14 手順8 / §18-3)。休暇の**実績**を見る画面。
 *
 * D-9 のカットオーバーで旧 `/leave/me`・`/leave/manage` を廃止した際、両画面が持っていた
 * 「申請」以外の機能 — 自分の残数 / 部下の残数一覧 / 年5日取得義務の未達アラート /
 * 年次有給休暇管理簿 (労基則24条の7の法定帳簿) — には新基盤側に代替が無かった。
 * 申請は `/requests` へ統合し、実績はこの画面が引き取る (§18-0 のカバー範囲突合の結果)。
 *
 * ⚠️ 「申請」と「実績」で可視性の担当機構が違う (§15 D-24)。この画面が扱うのは実績のため
 *    サーバー側は既存の `leave_visibility` ラダーで段階を決める。申請の項目表示制御
 *    (`request_field_visibility_*`) はこの画面には関与しない。
 * ⚠️ §3-4-0 H-4: 有休管理簿は `leave_grant:manage:all_stores` を持つ人のみ。サーバーも拒否するが、
 *    押しても必ず失敗するボタンを出さない (F-4/D-209 と同型の判断)。
 * ⚠️ 管理簿は呼出しごとに `sensitive_view_logs` へ export として記録されるため、
 *    常設テーブルとしては保持せずボタン押下時のみ取得する (§5-3 の設計方針を踏襲)。
 */

type FiveDayObligation = {
  grantId: string; obligationPeriodStart: string; obligationPeriodEnd: string;
  requiredDays: number; consumedDays: number; remainingDays: number; periodEnded: boolean;
};
type GrantBalance = { grant_id: string; grant_date: string; expires_on: string | null; remaining_days: number; remaining_minutes: number };
type BalanceRow = {
  leave_type_id: string; leave_type_label: string; balance_model: string | null;
  remaining_days: number; remaining_minutes: number;
  five_day_obligation: FiveDayObligation[]; grants: GrantBalance[];
};
type BalanceResponse = { has_data: boolean; as_of?: string; leave_types: BalanceRow[] };
type BalanceEmployee = { employee_id: string; employee_name: string; total_remaining_days: number; total_remaining_minutes: number };
type ObligationEntry = {
  grant_id: string; obligation_period_start: string; obligation_period_end: string;
  required_days: number; consumed_days: number; remaining_days: number; period_ended: boolean;
  at_risk: boolean; alert_days_threshold: number;
};
type ObligationEmployee = { employee_id: string; employee_name: string; obligations: ObligationEntry[] };
type RegisterRow = {
  employee_id: string; employee_name: string; grantId: string; grantDate: string; expiresOn: string | null;
  grantedDays: number | null; consumedDays: number; consumedDates: string[]; remainingDays: number | null;
};

function MyBalanceCards() {
  const { data, isLoading, isError } = useQuery<BalanceResponse>({
    queryKey: ['leave-me-balance'],
    queryFn: () => apiFetch<BalanceResponse>('/leave/me/balance'),
  });

  if (isLoading) return <div role="status" className="text-text-sub">読み込み中…</div>;
  if (isError) return <p role="alert" className="text-sm text-alert-text">残数の取得に失敗しました</p>;
  // ⚠️ design-expert Critical-2: `data === undefined` (§8-60 の paused 等) を「無い」と断定しない。
  if (!data) return <p role="status" className="text-sm text-text-sub">残数を取得できていません。再読み込みしてください。</p>;
  if (!data.has_data || data.leave_types.length === 0) {
    return <p className="text-sm text-text-sub">まだ付与された休暇がありません。</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {data.leave_types.map((t) => {
        const atRisk = t.five_day_obligation.find((o) => !o.periodEnded && o.remainingDays > 0);
        // 残数のある付与のうち最も早く時効を迎えるものを表示する (無期限の付与は除く)。
        const nearestExpiry = t.grants
          .filter((g) => g.expires_on !== null && (g.remaining_days > 0 || g.remaining_minutes > 0))
          .sort((a, b) => a.expires_on!.localeCompare(b.expires_on!))[0];
        // ⚠️ unlimited/fixed_calendar は付与行を作らない設計のため remaining_days が常に0になり
        //    「残数が無い」と誤読される。数値の残数ではなく専用表示にする。
        const isBoundless = t.balance_model === 'unlimited' || t.balance_model === 'fixed_calendar';
        return (
          <div key={t.leave_type_id} className="border border-surface-border rounded-lg bg-white p-4">
            <p className="text-sm text-text-sub">{t.leave_type_label}</p>
            {isBoundless ? (
              <p className="text-2xl font-bold text-text-body mt-1">
                {t.balance_model === 'unlimited' ? '無制限' : '会社が決定'}
              </p>
            ) : (
              <p className="text-2xl font-bold text-text-body tabular-nums mt-1">
                {t.remaining_days}<span className="text-sm font-normal ml-1">日</span>
              </p>
            )}
            {!isBoundless && t.remaining_minutes > 0 && (
              <p className="text-xs text-text-sub mt-0.5">
                時間単位残: {Math.floor(t.remaining_minutes / 60)}時間{t.remaining_minutes % 60}分
              </p>
            )}
            {nearestExpiry && (
              <p className="text-xs text-text-sub mt-0.5">次回時効: {nearestExpiry.expires_on} ({nearestExpiry.remaining_days}日分)</p>
            )}
            {atRisk && (
              <p className="text-xs text-alert-text mt-2">
                年5日取得義務: あと{atRisk.remainingDays}日 ({atRisk.obligationPeriodEnd}まで)
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TeamBalanceTable() {
  const { data, isLoading, isError } = useQuery<{ as_of: string; employees: BalanceEmployee[] }>({
    queryKey: ['leave-balances'],
    queryFn: () => apiFetch<{ as_of: string; employees: BalanceEmployee[] }>('/leave/balances'),
  });

  if (isLoading) return <div role="status" className="text-text-sub">読み込み中…</div>;
  if (isError) return <p role="alert" className="text-sm text-alert-text">残数一覧の取得に失敗しました</p>;
  if (!data) return <p role="status" className="text-sm text-text-sub">残数一覧を取得できていません。再読み込みしてください。</p>;
  const employees = data.employees;
  if (employees.length === 0) return <p className="text-sm text-text-sub">対象の社員がいません。</p>;

  return (
    <div className="border border-surface-border rounded-lg bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken text-text-sub">
            <tr>
              <th scope="col" className="relative text-left px-3 py-2 font-medium">氏名</th>
              <th scope="col" className="relative text-left px-3 py-2 font-medium">残数合計 (日)</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.employee_id} className="border-t border-surface-line">
                <td className="px-3 py-2">{e.employee_name}</td>
                <td className="px-3 py-2 tabular-nums">
                  {e.total_remaining_days}
                  {/* ⚠️ design-expert Major-5: 時間単位の残のみを持つ人が「0」と読まれないよう併記する */}
                  {e.total_remaining_minutes > 0 && (
                    <span className="ml-2 text-xs text-text-sub">
                      + {Math.floor(e.total_remaining_minutes / 60)}時間{e.total_remaining_minutes % 60}分
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 年5日未達アラート。`at_risk` (義務期間終了まで閾値日数以内かつ未達) のみを表示する。 */
function FiveDayObligationAlerts() {
  const { data, isLoading, isError } = useQuery<{ as_of: string; employees: ObligationEmployee[] }>({
    queryKey: ['leave-five-day-obligation'],
    queryFn: () => apiFetch<{ as_of: string; employees: ObligationEmployee[] }>('/leave/five-day-obligation'),
  });

  if (isLoading) return <div role="status" className="text-text-sub">読み込み中…</div>;
  if (isError) return <p role="alert" className="text-sm text-alert-text">年5日義務の進捗の取得に失敗しました</p>;
  // ⚠️ design-expert Critical-2: 未取得を「アラート無し」と断定してはならない。
  //    年5日取得義務 (労基法39条7項) の未達を偽って「無し」と表示するのは法令運用上許容できない。
  if (!data) {
    return <p role="status" className="text-sm text-text-sub">取得状況を確認できていません。再読み込みしてください。</p>;
  }

  const atRiskRows = data.employees
    .flatMap((e) => e.obligations.filter((o) => o.at_risk).map((o) => ({ employee: e, obligation: o })));

  if (atRiskRows.length === 0) return <p className="text-sm text-text-sub">未達アラートはありません。</p>;

  return (
    <ul className="space-y-2">
      {atRiskRows.map(({ employee, obligation }) => (
        <li key={obligation.grant_id} className="flex items-start gap-2 border border-alert-border bg-alert-bg rounded-lg p-3">
          <AlertTriangle size={16} className="text-alert-text mt-0.5 shrink-0" aria-hidden />
          <div className="text-sm flex-1 min-w-0">
            <p className="font-medium text-text-body">{employee.employee_name}</p>
            <p className="text-text-sub">
              {obligation.obligation_period_end} までに残り {obligation.remaining_days} 日
              (取得 {obligation.consumed_days}/{obligation.required_days} 日)
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function LeaveRegisterExport() {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const body = await apiFetch<{ leave_type_id: string | null; leave_type_label: string | null; rows: RegisterRow[] }>('/leave/register');
      if (!body.leave_type_id || body.rows.length === 0) {
        toast.error('出力できるデータがありません (年次有給休暇の休暇種別・付与が未登録です)');
        return;
      }
      downloadAsCsv(
        `leave-register-${new Date().toISOString().slice(0, 10)}.csv`,
        ['employee_id', 'employee_name', 'grantDate', 'expiresOn', 'grantedDays', 'consumedDays', 'consumedDates', 'remainingDays'],
        body.rows.map((r) => ({
          employee_id: r.employee_id, employee_name: r.employee_name, grantDate: r.grantDate,
          expiresOn: r.expiresOn ?? '', grantedDays: r.grantedDays ?? '', consumedDays: r.consumedDays,
          consumedDates: r.consumedDates.join('; '), remainingDays: r.remainingDays ?? '',
        })),
      );
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '有休管理簿の取得に失敗しました');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button variant="secondary" size="sm" loading={isDownloading} leftIcon={<Download size={14} />} onClick={handleDownload}>
      CSVダウンロード
    </Button>
  );
}

export function LeaveRecordsPage() {
  const user = useAuthStore((s) => s.user);
  // 部下の残数一覧・年5日アラートは他者の実績を見る操作。
  // ⚠️ フロントの EffectivePermission は feature/action しか持たず scope を判別できない。
  //    `leave:manage` は permissions に self 行が存在しない (all/node/own のみ) ため、
  //    「他者に手が届く人か」の代理判定として使える (RequestsPage の canRetry と同じ判断)。
  //    view で判定すると self しか持たないスタッフにも「部下の残数一覧」が出て、
  //    自分1人だけが並ぶ意味の分からない表になる。
  const canViewOthers = hasPermission(user?.effective_permissions, 'leave', 'manage', user?.non_inheriting_features);
  // ⚠️ design-and-code M-7: 年5日未達アラートの API (`GET /leave/five-day-obligation`) が要求するのは
  //    `leave:manage` ではなく `leave_grant:view`。`leave:manage` で出し分けると
  //    「押せる (見えている) のに必ず 403」になる (F-4/D-209 と同型)。別々に判定する。
  //    ⚠️ leave_grant:view には self 行が実在するため、これだけでは「他者に手が届く人か」は
  //    判定できない。他者向けセクションであることは canViewOthers と併せて担保する。
  const canViewGrants = hasPermission(user?.effective_permissions, 'leave_grant', 'view', user?.non_inheriting_features);
  // ⚠️ TD-22: 有休管理簿 (GET /leave/register) は requireTenantWideGrantManage
  // (routes/leave-grants.ts) で全店舗スコープを要求するため、テナント全体権限で判定する。
  const canManageGrants = hasTenantWidePermission(user?.effective_permissions, 'leave_grant', 'manage', user?.non_inheriting_features);

  return (
    <div className="space-y-4">
      <h1 className="text-h1">休暇の残数と記録</h1>
      <p className="text-sm text-text-sub">
        休暇の申請は「申請」画面から行います。この画面では残数・取得状況・管理簿を確認します。
      </p>

      <section className="space-y-2">
        <h2 className="text-h2">自分の残数</h2>
        <MyBalanceCards />
      </section>

      {canViewOthers && (
        <>
          <section className="space-y-2">
            <h2 className="text-h2">部下の残数一覧</h2>
            <TeamBalanceTable />
          </section>
        </>
      )}

      {canViewOthers && canViewGrants && (
        <section className="space-y-2">
          <h2 className="text-h2">年5日未達アラート</h2>
          <FiveDayObligationAlerts />
        </section>
      )}

      {canManageGrants && (
        <section className="space-y-2">
          <h2 className="text-h2">有休管理簿</h2>
          <p className="text-sm text-text-sub">
            年次有給休暇の基準日・取得日数・取得時季を CSV で出力します (労働基準法施行規則24条の7の管理簿)。
          </p>
          <LeaveRegisterExport />
        </section>
      )}
    </div>
  );
}
